import { fetchSesameToken } from '@/modules/api';

/**
 * Hand the sesame token to the native shell: Android exposes a `handleAuth`
 * method, iOS a `handleAuth` WKWebView message handler (Sparta parity —
 * sparta/src/user/utils/authorizationHandoff.ts). Returns the bridge that
 * received the token, or null when neither is present.
 */
function handSesameToNative(token: string): 'android' | 'webkit' | null {
  if (typeof window === 'undefined') return null;

  if (window.android?.handleAuth) {
    window.android.handleAuth(token);
    return 'android';
  }

  if (window.webkit?.messageHandlers?.handleAuth?.postMessage) {
    window.webkit.messageHandlers.handleAuth.postMessage(token);
    return 'webkit';
  }

  return null;
}

/**
 * Exit-side auth handoff for native-WebView onboarding, done entirely in-app.
 *
 * Mints a sesame token from the app's own Bearer (Apigee verifies the JWT and
 * injects a Django session, so no cross-origin `sessionid` cookie is involved)
 * and passes it to native via the `handleAuth` bridge, which native exchanges
 * for a fresh legacy JWT. Works identically on iOS and Android, and for
 * anonymous "Try Vida Now" users.
 *
 * Returns true only when the sesame was handed to native — so the caller knows
 * to allow the head-start delay before closing. On any miss (mint failed, no
 * bridge) it returns false; the caller closes the WebView regardless.
 */
export async function handoffSesameToNative(): Promise<boolean> {
  const token = await fetchSesameToken();
  if (!token) return false;

  return handSesameToNative(token) !== null;
}
